/**
 * backend.js — single HTTP client for all backend API calls.
 * All data goes through BigQuery via the backend; no Sheets reads/writes.
 */

const BASE = import.meta.env.VITE_BACKEND_BASE_URL;
if (!BASE) throw new Error('VITE_BACKEND_BASE_URL is not set');

async function req(getToken, path, options = {}) {
  const token = await getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = '';
    try { msg = (await res.json())?.error ?? ''; } catch (_) {}
    if (!msg) msg = res.statusText || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return res.json();
}

/** Like req(), but returns a Blob instead of parsing JSON — for file downloads. */
async function fetchBlob(getToken, path) {
  const token = await getToken();
  const res = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let msg = '';
    try { msg = (await res.json())?.error ?? ''; } catch (_) {}
    throw new Error(msg || res.statusText || `HTTP ${res.status}`);
  }
  return res.blob();
}

// ── Bulk data load ────────────────────────────────────────────────────────────

/**
 * Today-page bundle — one round-trip instead of 6.
 * Returns { contacts, firms, demos, trials, outreachLog, config }.
 * Contacts are pre-filtered to active-pipeline + 500 cold-untouched,
 * so the payload is much smaller than fetchAllContacts(?limit=50000).
 */
export function fetchTodayBundle(getToken) {
  return req(getToken, '/api/today?outreach_days=14');
}

// ── Data reads ────────────────────────────────────────────────────────────────

/** Fetch all contacts (no limit; server cache makes this fast after the first load). */
export function fetchAllContacts(getToken) {
  return req(getToken, '/api/contacts?limit=50000&sort=added_date&dir=desc');
}

/** Fetch lightweight email→contact map for ALL contacts (no row cap). Used for attendee resolution. */
export function fetchContactEmailMap(getToken) {
  return req(getToken, '/api/contacts/email-map');
}

/** Fetch attendees from active demos that have no matching contact — BigQuery anti-join, fast at any scale. */
export function fetchCalendarUnknownAttendees(getToken) {
  return req(getToken, '/api/calendar/unknown-attendees');
}

/** Fetch all firms. Returns array directly (no pagination wrapper). */
export function fetchAllFirms(getToken) {
  return req(getToken, '/api/firms');
}

/** Fetch all demos (no cap; server cache makes this fast after the first load). */
export function fetchAllDemos(getToken) {
  return req(getToken, '/api/demos?limit=50000');
}

/** Fetch outreach log. Returns { rows }. limit=0 means no cap.
 *  sinceDays: when fetching the full log (no contactId), only fetch rows this many days back.
 *  Pass sinceDays=0 to disable the window (full history). Default 180 days. */
export function fetchOutreachLog(getToken, { limit = 0, contactId = '', sinceDays = 180 } = {}) {
  const p = new URLSearchParams({ limit: String(limit) });
  if (contactId) {
    p.set('contactId', contactId);
  } else if (sinceDays > 0) {
    const since = new Date();
    since.setDate(since.getDate() - sinceDays);
    p.set('since', since.toISOString().slice(0, 10));
  }
  return req(getToken, `/api/outreach?${p}`);
}

/** Manually override reply_type for a gmail thread. */
export function patchReplyType(getToken, threadId, replyType) {
  return req(getToken, '/api/outreach/reply-type', {
    method: 'PATCH',
    body: JSON.stringify({ threadId, replyType }),
  });
}

/** Fetch aggregated analytics (stage counts + source stats). */
export function fetchAnalytics(getToken) {
  return req(getToken, '/api/analytics');
}

export function fetchAmplitudeStats(getToken, mode = 'trial') {
  return req(getToken, `/api/amplitude-stats?mode=${mode}`);
}

export function syncAmplitudeContacts(getToken) {
  return req(getToken, '/api/sync-amplitude-contacts', { method: 'POST', body: '{}' });
}

/**
 * Move a contact to a different firm, keeping the old firm's name on the
 * contact as previous_firm — same job-change history logic as the trial merge.
 */
export function moveContactFirm(getToken, contactId, firmId) {
  return req(getToken, `/api/contacts/${encodeURIComponent(contactId)}/move-firm`, {
    method: 'POST', body: JSON.stringify({ firm_id: firmId }),
  });
}

/** Fetch a single contact (with _firm_name). Works for contacts outside the /api/today bundle. */
export function getContact(getToken, contactId) {
  return req(getToken, `/api/contacts/${encodeURIComponent(contactId)}`);
}

/**
 * Merge a Clerk trial row into an existing contact whose email is out of date
 * (IMP-1816 job-change flow). Updates the contact's email to the trial's Clerk
 * email (old one kept as previous_email), optionally moves them to firm_id
 * (old firm name kept as previous_firm), and links the trial row.
 */
export function mergeTrialContact(getToken, trialId, { contact_id, firm_id = '' }) {
  return req(getToken, `/api/trials/${encodeURIComponent(trialId)}/merge-contact`, {
    method: 'POST', body: JSON.stringify({ contact_id, firm_id }),
  });
}

/**
 * Fold a Clerk-auto-created customer contact (sourceContactId) into the
 * existing CRM contact it duplicates (IMP-1811 job-change flow, customer
 * counterpart of mergeTrialContact). The target contact takes the Clerk email
 * (old one kept as previous_email), optionally moves to firm_id, inherits the
 * Clerk link + customer stage; the auto-created duplicate is deleted.
 * swap = true reverses the direction: the target keeps its own email/firm and
 * the Clerk email/firm become previous_email / previous_firm (for when the
 * Clerk signup is the older identity).
 * no_longer_customer = true: the subscription stayed at the fund they left —
 * the survivor is not marked customer (stage recomputes) and the Clerk
 * account is flagged churned.
 */
export function mergeClerkContact(getToken, sourceContactId, { contact_id, firm_id = '', swap = false, no_longer_customer = false }) {
  return req(getToken, `/api/contacts/${encodeURIComponent(sourceContactId)}/merge-clerk-contact`, {
    method: 'POST', body: JSON.stringify({ contact_id, firm_id, swap, no_longer_customer }),
  });
}

/**
 * Dismiss a wrong name-match merge suggestion (two different people share a
 * name): the Clerk-created contact stays as its own separate contact, the
 * ✦ Suggested chip goes away, and firm_id (when given) links its firm.
 */
export function dismissMergeSuggestion(getToken, contactId, { firm_id = '' } = {}) {
  return req(getToken, `/api/contacts/${encodeURIComponent(contactId)}/dismiss-suggestion`, {
    method: 'POST', body: JSON.stringify({ firm_id }),
  });
}

export function searchContacts(getToken, q) {
  return req(getToken, `/api/contacts/search?q=${encodeURIComponent(q)}`);
}

export function fetchContactsByIds(getToken, ids) {
  return req(getToken, '/api/contacts/filter', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ include_ids: ids, limit: ids.length, sort: 'name', dir: 'asc' }),
  }).then((r) => r.rows ?? []);
}

export function linkEmailToContact(getToken, contactId, email) {
  return req(getToken, `/api/contacts/${contactId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

/** Fetch config key/value map. Returns plain object. */
export function fetchConfig(getToken) {
  return req(getToken, '/api/config');
}

/** Update config key/value map — single PATCH request for all keys at once. */
export async function updateConfig(getToken, fields) {
  await req(getToken, '/api/config', { method: 'PATCH', body: JSON.stringify(fields) });
}

/** Fetch pending gmail-unmatched rows. Returns array. */
export function fetchGmailUnmatched(getToken) {
  return req(getToken, '/api/gmail-unmatched');
}

// ── Contacts CRUD ─────────────────────────────────────────────────────────────

export function createContact(getToken, contact) {
  return req(getToken, '/api/contacts', { method: 'POST', body: JSON.stringify(contact) });
}

export function updateContact(getToken, id, fields) {
  return req(getToken, `/api/contacts/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) });
}

// Hard delete — row removed outright. Only for dedup cleanup (merging
// duplicate rows): a hard-deleted contact CAN be re-created by the next BD
// import. To delete a *person*, use offboardContact instead.
export function deleteContact(getToken, id) {
  return req(getToken, `/api/contacts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Soft-delete a user everywhere (IMP-1833): stage → 'deleted' (hidden from
// every page/export/count), trial rows removed, Clerk account locked, and
// BD import / Clerk roster sync will never re-add them.
export function offboardContact(getToken, id) {
  return req(getToken, `/api/contacts/${encodeURIComponent(id)}/offboard`, { method: 'POST' });
}

/** Save raw notes for a contact and trigger AI tag+summary extraction. Returns { ok, ai_tags, ai_summary, ai_error? }. */
export function saveContactNotes(getToken, id, notes) {
  return req(getToken, `/api/contacts/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
}

/** Fetch all inbound emails for sentiment review. sentiment=unclassified|positive|... or omit for all. */
export function fetchSentimentInbox(getToken, { days = 180, limit = 500, sentiment = '' } = {}) {
  const p = new URLSearchParams({ days, limit });
  if (sentiment) p.set('sentiment', sentiment);
  return req(getToken, `/api/outreach/sentiment-inbox?${p}`);
}

/** Fetch the full Gmail thread replay for a given thread id. */
export function fetchOutreachThread(getToken, threadId) {
  return req(getToken, `/api/outreach/thread/${encodeURIComponent(threadId)}`);
}

/** Override reply_sentiment for a specific outreach log row. */
export function patchReplySentiment(getToken, { logId, threadId, sentiment }) {
  return req(getToken, '/api/outreach/sentiment', {
    method: 'PATCH',
    body: JSON.stringify({ logId, threadId, sentiment }),
  });
}

/** Recompute the AI deal score for one contact. Returns the deal record. */
export function refreshDealScore(getToken, contactId) {
  return req(getToken, `/api/deal-score/${encodeURIComponent(contactId)}`, { method: 'POST' });
}

/** Batch-score all eligible contacts (gated + batched server-side). */
export function backfillDealScores(getToken, { limit = 500 } = {}) {
  return req(getToken, '/api/deal-score/backfill', {
    method: 'POST',
    body: JSON.stringify({ limit }),
  });
}

/** Sentiment classifier accuracy metric (share of AI labels not later overridden). */
export function fetchSentimentAccuracy(getToken, days = 90) {
  return req(getToken, `/api/sentiment-accuracy?days=${days}`);
}

/** Drill into an analytics segment. type=sentiment|funnel|weekly|unsub, value=the segment label. */
export function fetchAnalyticsDrill(getToken, type, value, days = 90) {
  const p = new URLSearchParams({ type, days });
  if (value != null) p.set('value', String(value));
  return req(getToken, `/api/outreach/analytics/drill?${p}`);
}

// ── Firms CRUD ────────────────────────────────────────────────────────────────

export function lookupFirmByDomain(getToken, domain) {
  return req(getToken, `/api/firms/lookup-by-domain?domain=${encodeURIComponent(domain)}`);
}

export function createFirm(getToken, firm) {
  return req(getToken, '/api/firms', { method: 'POST', body: JSON.stringify(firm) });
}

export function updateFirm(getToken, id, fields) {
  return req(getToken, `/api/firms/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) });
}

export function deleteFirm(getToken, id) {
  return req(getToken, `/api/firms/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function syncFirmScores(getToken) {
  return req(getToken, '/api/firms/sync-scores', { method: 'POST', body: '{}' });
}

export function syncContactsSheetToBq(getToken) {
  return req(getToken, '/api/sync/contacts/sheet-to-bq', { method: 'POST', body: '{}' });
}
export function syncContactsBqToSheet(getToken) {
  return req(getToken, '/api/sync/contacts/bq-to-sheet', { method: 'POST', body: '{}' });
}
export function getSyncConfig(getToken) {
  return req(getToken, '/api/sync/contacts/config', { method: 'GET' });
}
export function saveSyncConfig(getToken, body) {
  return req(getToken, '/api/sync/contacts/config', { method: 'POST', body: JSON.stringify(body) });
}
export function syncContactsSheetToBqWithOpts(getToken, body) {
  return req(getToken, '/api/sync/contacts/sheet-to-bq', { method: 'POST', body: JSON.stringify(body) });
}
export function syncContactsBqToSheetWithOpts(getToken, body) {
  return req(getToken, '/api/sync/contacts/bq-to-sheet', { method: 'POST', body: JSON.stringify(body) });
}
export function mergeContactsWithOpts(getToken, body) {
  return req(getToken, '/api/sync/contacts/merge', { method: 'POST', body: JSON.stringify(body) });
}
export function syncFirmsSheetToBq(getToken) {
  return req(getToken, '/api/sync/firms/sheet-to-bq', { method: 'POST', body: '{}' });
}
export function syncFirmsBqToSheet(getToken) {
  return req(getToken, '/api/sync/firms/bq-to-sheet', { method: 'POST', body: '{}' });
}

// ── Demos ─────────────────────────────────────────────────────────────────────

export function updateDemo(getToken, id, fields) {
  return req(getToken, `/api/demos/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) });
}

export function deleteDemos(getToken, ids) {
  return req(getToken, '/api/demos', { method: 'DELETE', body: JSON.stringify({ ids }) });
}

// ── Trials ────────────────────────────────────────────────────────────────────

export function createTrial(getToken, trial) {
  return req(getToken, '/api/trials', { method: 'POST', body: JSON.stringify(trial) });
}

export function updateTrial(getToken, id, fields) {
  return req(getToken, `/api/trials/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) });
}

/**
 * Delete trial rows. With lockClerk, each row's Implied app account is also
 * locked and flagged lifecycle='removed' in Clerk so the roster merge can't
 * resurrect the row (a plain delete of a roster-synced trial comes back on
 * the next merge).
 */
export function deleteTrials(getToken, ids, { lockClerk = false } = {}) {
  return req(getToken, '/api/trials', { method: 'DELETE', body: JSON.stringify({ ids, lock_clerk: lockClerk }) });
}

/** Add a quick trial note; optionally logs it to the contact timeline and/or a follow-up date. */
export function apiAddTrialNote(getToken, trialId, note) {
  return req(getToken, `/api/trials/${encodeURIComponent(trialId)}/notes`, { method: 'POST', body: JSON.stringify(note) });
}

/** List quick notes for a trial, newest first. */
export function apiGetTrialNotes(getToken, trialId) {
  return req(getToken, `/api/trials/${encodeURIComponent(trialId)}/notes`);
}

/** Convert a trial to a customer — updates trial + contact stage + timeline + deal score in one call. */
export function apiConvertTrial(getToken, trialId, payload) {
  return req(getToken, `/api/trials/${encodeURIComponent(trialId)}/convert`, { method: 'POST', body: JSON.stringify(payload) });
}

/** Mark a trial as churned — updates trial + timeline + (optionally) contact stage + deal score in one call. */
export function apiChurnTrial(getToken, trialId, payload) {
  return req(getToken, `/api/trials/${encodeURIComponent(trialId)}/churn`, { method: 'POST', body: JSON.stringify(payload) });
}

/**
 * Unlock a churned trial's Clerk account and clear its scheduled lock —
 * the "they came back" path (IMP-1808).
 */
export function apiUnlockTrial(getToken, trialId) {
  return req(getToken, `/api/trials/${encodeURIComponent(trialId)}/unlock`, { method: 'POST' });
}

/** Schedule (or reschedule) a churned trial's account lock for a given date. */
export function apiLockTrial(getToken, trialId, lockedDate) {
  return req(getToken, `/api/trials/${encodeURIComponent(trialId)}/lock`, { method: 'POST', body: JSON.stringify({ locked_date: lockedDate }) });
}

/**
 * Set a Clerk product user's lifecycle flag ('trial' | 'converted' | 'churned').
 * Pass { clerk_user_id } or { email }; optional trial_id stamps the flag onto
 * the BQ trial row too. Used right after creating a product user.
 */
export function apiFlagClerkUser(getToken, payload) {
  return req(getToken, '/api/trials/flag-clerk-user', { method: 'POST', body: JSON.stringify(payload) });
}

/**
 * Force the Clerk roster merge now (bypasses its throttle) and bust the
 * server-side caches, so a follow-up data refresh reflects Clerk immediately.
 * Returns { ok, trials, clerk_linked }.
 */
export function apiSyncClerkRoster(getToken) {
  return req(getToken, '/api/trials/sync-clerk', { method: 'POST' });
}

/** Latest Amplitude usage journal for a trial: { configured, running, latest }. */
export function apiGetTrialUsageJournal(getToken, trialId) {
  return req(getToken, `/api/trials/${encodeURIComponent(trialId)}/usage-journal`);
}

/** Kick off usage-journal generation (backend responds 202 and works in the background).
 *  eventsOnly: refresh just the Amplitude export (heatmap data) — no LLM narrative.
 *  days: export window size (backend default 30, clamped 7–190). */
export function apiGenerateTrialUsageJournal(getToken, trialId, { eventsOnly = false, days } = {}) {
  return req(getToken, `/api/trials/${encodeURIComponent(trialId)}/usage-journal`, {
    method: 'POST', body: JSON.stringify({ events_only: eventsOnly, ...(days ? { days } : {}) }),
  });
}

// ── Contracts ─────────────────────────────────────────────────────────────────

export function fetchContracts(getToken) {
  return req(getToken, '/api/contracts');
}

export function createContract(getToken, contract) {
  return req(getToken, '/api/contracts', { method: 'POST', body: JSON.stringify(contract) });
}

/** Creates a bare contract AND links it to an invoice in one BigQuery script — half the round-trip of doing both separately. */
export function createContractAndLinkInvoice(getToken, payload) {
  return req(getToken, '/api/contracts/create-and-link', { method: 'POST', body: JSON.stringify(payload) });
}

export function updateContract(getToken, id, fields) {
  return req(getToken, `/api/contracts/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) });
}

export function deleteContract(getToken, id) {
  return req(getToken, `/api/contracts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Uploads a signed contract PDF/scan. Bypasses req() — needs multipart, not JSON. */
export async function uploadContractFile(getToken, contractId, file) {
  const token = await getToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/api/contracts/${encodeURIComponent(contractId)}/upload-file`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    let msg = '';
    try { msg = (await res.json())?.error ?? ''; } catch (_) {}
    throw new Error(msg || res.statusText || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Attaches a pasted Google Drive (or any external) share link instead of uploading a file. */
export function linkContractFile(getToken, contractId, url) {
  return req(getToken, `/api/contracts/${encodeURIComponent(contractId)}/link-file`, { method: 'POST', body: JSON.stringify({ url }) });
}

/** Fetches a GCS-uploaded contract file as a Blob (gs:// isn't browser-fetchable directly). */
export async function downloadContractFileBlob(getToken, contractId) {
  return fetchBlob(getToken, `/api/contracts/${encodeURIComponent(contractId)}/download`);
}

// ── Raw usage export ──────────────────────────────────────────────────────────

/**
 * Streams one CSV row per product event for a firm's users (sub-funds
 * included by default) as a Blob — the fund-level replacement for the
 * clients-app /usage page's per-org "Download Raw Data" (same date /
 * event_type / user_id shape, plus a firm column).
 */
export function downloadFirmUsageCsvBlob(getToken, firmId, { start, end, includeSubfunds = true } = {}) {
  return fetchBlob(getToken, `/api/firms/${encodeURIComponent(firmId)}/usage-export?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&include_subfunds=${includeSubfunds ? 1 : 0}`);
}

/**
 * Fund × feature usage matrix for a date window (sub-funds rolled up into
 * parents): { features, rows: [{fund_name, counts, total, users}],
 * feature_totals, grand_total, total_users }.
 */
export function fetchUsageSummary(getToken, { start, end } = {}) {
  return req(getToken, `/api/usage-summary?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
}

// ── Invoices ──────────────────────────────────────────────────────────────────
// 1:1 with the "Invoice Tracker" sheet in Invoice_generator.xlsm.

export function fetchInvoices(getToken) {
  return req(getToken, '/api/invoices');
}

/** The "RECORD INVOICE" action: fills the PDF template, uploads it, and inserts the tracker row in one call. */
export function generateInvoice(getToken, invoice) {
  return req(getToken, '/api/invoices/generate', { method: 'POST', body: JSON.stringify(invoice) });
}

export function updateInvoice(getToken, id, fields) {
  return req(getToken, `/api/invoices/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(fields) });
}

export function deleteInvoice(getToken, id) {
  return req(getToken, `/api/invoices/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Fetches the generated invoice PDF as a Blob (gs:// isn't browser-fetchable directly). */
export async function downloadInvoicePdfBlob(getToken, id) {
  return fetchBlob(getToken, `/api/invoices/${encodeURIComponent(id)}/download`);
}

/** Manually attaches (or overwrites) an invoice PDF — e.g. the client's own signed copy instead of the auto-generated one. */
export async function uploadInvoiceFile(getToken, id, file) {
  const token = await getToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${BASE}/api/invoices/${encodeURIComponent(id)}/upload-file`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    let msg = '';
    try { msg = (await res.json())?.error ?? ''; } catch (_) {}
    throw new Error(msg || res.statusText || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Attaches a pasted Google Drive (or any external) link as the invoice record instead of uploading a file. */
export function linkInvoiceFile(getToken, id, url) {
  return req(getToken, `/api/invoices/${encodeURIComponent(id)}/link-file`, { method: 'POST', body: JSON.stringify({ url }) });
}

// ── Billing clients (replaces the "Client Tracker" sheet) ─────────────────────

export function fetchBillingClients(getToken) {
  return req(getToken, '/api/billing-clients');
}

export function saveBillingClient(getToken, client) {
  return req(getToken, '/api/billing-clients', { method: 'POST', body: JSON.stringify(client) });
}

// ── Contracts (minimal — powers the Invoice "Contract" dropdown) ──────────────

export function fetchContractsLite(getToken) {
  return req(getToken, '/api/contracts-lite');
}

// ── Outreach log (manual touchpoint write) ────────────────────────────────────

export function appendOutreachLog(getToken, row) {
  return req(getToken, '/api/outreach-log', { method: 'POST', body: JSON.stringify(row) });
}

// ── Gmail unmatched ───────────────────────────────────────────────────────────

export function dismissUnmatchedEmail(getToken, email) {
  return req(getToken, `/api/gmail-unmatched/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'dismissed' }),
  });
}

export function deleteUnmatchedEmail(getToken, email) {
  return req(getToken, `/api/gmail-unmatched/${encodeURIComponent(email)}`, { method: 'DELETE' });
}

// ── Paginated contacts/firms (used by Contacts page) ─────────────────────────

export function fetchContactsPage(getToken, { page = 1, limit = 200, sort = 'name', dir = 'asc', q = '', filters = [] } = {}) {
  const p = new URLSearchParams({ page: String(page), limit: String(limit), sort, dir, q, filters: JSON.stringify(filters) });
  return req(getToken, `/api/contacts?${p}`);
}

export function fetchFirmsPage(getToken, { page = 1, limit = 200, sort = 'firm_name', dir = 'asc', q = '', filters = [] } = {}) {
  const p = new URLSearchParams({ page: String(page), limit: String(limit), sort, dir, q, filters: JSON.stringify(filters) });
  return req(getToken, `/api/firms?${p}`);
}

export function fetchFirmContacts(getToken, firmName) {
  const p = new URLSearchParams({ firm_name: String(firmName ?? '') });
  return req(getToken, `/api/firms/contacts?${p}`);
}

// ── BD Campaign Analytics ─────────────────────────────────────────────────────

export function fetchCampaignStats(getToken, responseDays = 7) {
  return req(getToken, `/api/bd-campaign-stats?response_days=${responseDays}`);
}

export function fetchGroupStats(getToken, responseDays = 7) {
  return req(getToken, `/api/bd-group-stats?response_days=${responseDays}`);
}

export function fetchSendTimeStats(getToken) {
  return req(getToken, `/api/bd-send-time-stats`);
}

export function fetchCampaignContacts(getToken, uploadId, responseDays = 7) {
  return req(getToken, `/api/bd-campaign-contacts/${encodeURIComponent(uploadId)}?response_days=${responseDays}`);
}

export function fetchBdHighIntent(getToken, days = 14) {
  return req(getToken, `/api/bd-high-intent?days=${days}`);
}

export function fetchBdHighIntentExport(getToken, start, end) {
  return req(getToken, `/api/bd-high-intent-export?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
}

export function tagContacts(getToken, contactIds, tag, action = 'add') {
  return req(getToken, `/api/contacts/tag`, {
    method: 'PATCH',
    body: JSON.stringify({ contact_ids: contactIds, tag, action }),
  });
}

export function fetchBdFollowupList(getToken, uploadId) {
  return req(getToken, `/api/bd-followup-list/${encodeURIComponent(uploadId)}`);
}

// ── Contact Views (Saved Filter Presets) ─────────────────────────────────────

export function fetchContactViews(getToken) {
  return req(getToken, '/api/contact-views');
}

export function createContactView(getToken, view) {
  return req(getToken, '/api/contact-views', { method: 'POST', body: JSON.stringify(view) });
}

export function updateContactView(getToken, id, patch) {
  return req(getToken, `/api/contact-views/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(patch) });
}

export function deleteContactView(getToken, id) {
  return req(getToken, `/api/contact-views/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── GTM ───────────────────────────────────────────────────────────────────────

/** Classify recent inbound replies with Claude Haiku. Returns { classified: N }. */
export function classifyReplies(getToken, days = 30) {
  return req(getToken, '/api/classify-replies', { method: 'POST', body: JSON.stringify({ days }) });
}

/** Fetch the weekly outreach queue — contacts ready to be reached out to. */
export function fetchOutreachQueue(getToken, { limit = 200, stage = '' } = {}) {
  const p = new URLSearchParams({ limit: String(limit) });
  if (stage) p.set('stage', stage);
  return req(getToken, `/api/outreach/queue?${p}`);
}

/** Fetch GTM analytics — weekly volume, batch reply rates, sentiment, funnel. */
export function fetchOutreachAnalytics(getToken, days = 90) {
  return req(getToken, `/api/outreach/analytics?days=${days}`);
}

/** Save next_action + next_action_due for a contact. */
export function saveNextAction(getToken, contact_id, next_action, next_action_due) {
  return req(getToken, '/api/outreach/next-action', {
    method: 'PATCH',
    body: JSON.stringify({ contact_id, next_action, next_action_due }),
  });
}

/** Mark a contact as excluded from GTM outreach (not ICP). */
export function excludeContact(getToken, contact_id, reason = '') {
  return req(getToken, `/api/contacts/${encodeURIComponent(contact_id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ gtm_excluded: true, gtm_exclude_reason: reason }),
  });
}

/** Pin/unpin a contact to the outreach queue (surfaces regardless of history). */
export function setContactPinned(getToken, contact_id, pinned = true) {
  return req(getToken, `/api/contacts/${encodeURIComponent(contact_id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ gtm_pinned: pinned }),
  });
}

/** Manually set (or clear with '') a contact's outreach lane override. */
export function setContactLane(getToken, contact_id, lane = '') {
  return req(getToken, `/api/contacts/${encodeURIComponent(contact_id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ outreach_lane: lane }),
  });
}

/** Ignore a contact — hidden from the outreach queue until new activity. */
export function ignoreContact(getToken, contact_id, ignored = true) {
  return req(getToken, '/api/outreach/ignore', {
    method: 'POST',
    body: JSON.stringify({ contact_id, ignored }),
  });
}

/** Re-run Haiku sentiment classification on all inbound emails that have a body. */
export function reclassifyAllSentiment(getToken, { days = 365, force = false } = {}) {
  return req(getToken, '/api/reclassify-sentiment', {
    method: 'POST',
    body: JSON.stringify({ days, force }),
  });
}

/** Fill blank status/geography/AUM fields on contacts from their firm or sibling contacts. */
export function backfillFirmDefaults(getToken) {
  return req(getToken, '/api/backfill-firm-defaults', { method: 'POST' });
}

export function dedupOutreachLog(getToken) {
  return req(getToken, '/api/dedup-outreach-log', { method: 'POST' });
}

/** Backfill Gmail email bodies for inbound rows missing body text. */
export function backfillGmailBodies(getToken, { days = 90, limit = 50 } = {}) {
  return req(getToken, '/api/backfill-gmail-bodies', {
    method: 'POST',
    body: JSON.stringify({ days, limit }),
  });
}

/** AI natural language filter/rerank of outreach contacts via Claude Haiku. */
export function aiFilterOutreach(getToken, query, contacts, icp_definition = '') {
  return req(getToken, '/api/outreach/ai-filter', {
    method: 'POST',
    body: JSON.stringify({ query, icp_definition, contacts }),
  });
}

// ── Cold-outreach cadence (G1-16 style follow-up tracking) ───────────────────

/** Fetch every tracked list's timeline + the flattened "what's due" action list. */
export function fetchCadence(getToken) {
  return req(getToken, '/api/cadence');
}

/** Save draft subject/body for a not-yet-sent (list_name, stage). */
export function saveCadenceContent(getToken, listName, stage, { subject, body }) {
  return req(getToken, `/api/cadence/${encodeURIComponent(listName)}/${stage}/content`, {
    method: 'PUT',
    body: JSON.stringify({ subject, body }),
  });
}

/** Manually mark a (list_name, stage) sent/unsent — overrides the computed status everywhere. */
export function setCadenceStageSent(getToken, listName, stage, sent, sentAt) {
  return req(getToken, `/api/cadence/${encodeURIComponent(listName)}/${stage}/sent`, {
    method: 'PUT',
    body: JSON.stringify({ sent, sentAt: sentAt ? new Date(sentAt).toISOString() : undefined }),
  });
}

/** Correct which stage a real send (bd_uploads row) counts as — for when the
 * automatic date-order inference gets it wrong. Pass stage: null to clear
 * the override and go back to trusting the automatic inference. */
export function setCadenceUploadStage(getToken, uploadId, listName, stage) {
  return req(getToken, `/api/cadence/uploads/${encodeURIComponent(uploadId)}/stage`, {
    method: 'PUT',
    body: JSON.stringify({ listName, stage }),
  });
}

// ── Daily Digest ──────────────────────────────────────────────────────────────

/** Fetch digest JSON (urgentItems, watchItems, snapshot). */
export function fetchDailyDigest(getToken) {
  return req(getToken, '/api/daily-digest');
}

/** Fetch rendered HTML preview of today's digest (returns text, not JSON). */
export async function fetchDailyDigestPreview(getToken) {
  const token = await getToken();
  const res = await fetch(`${BASE}/api/daily-digest-preview`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.text();
}

/** Send the digest. Pass a curated digest object to send only selected items. */
export function sendDailyDigestNow(getToken, digest = null, miscNotes = '', recipients = '') {
  return req(getToken, '/api/send-daily-digest', {
    method: 'POST',
    body: JSON.stringify({ ...(digest ? { digest } : {}), miscNotes, ...(recipients ? { recipients } : {}) }),
  });
}

export function fetchZoomNotesByDemo(getToken, demoId) {
  return req(getToken, `/api/zoom-meeting-notes/by-demo/${encodeURIComponent(demoId)}`);
}

export function fetchZoomNotesByContact(getToken, contactId) {
  return req(getToken, `/api/zoom-meeting-notes/by-contact/${encodeURIComponent(contactId)}`);
}
